import { useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from '@/common/components/skeleton/Skeleton';
import {
	MediaImage,
	MediaImageSkeletonWrap,
	MediaImageSlot,
} from './mediaImage.styles';

const MediaImageLoader = ({
	src,
	alt,
	webpSrc,
	objectPosition = 'center center',
	priority = false,
	loading = 'lazy',
	className,
}) => {
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [hasImageError, setHasImageError] = useState(false);
	const imageRef = useRef(null);
	const showImageSkeleton = !src || hasImageError || !isImageLoaded;

	useEffect(() => {
		setIsImageLoaded(false);
		setHasImageError(false);
	}, [src, webpSrc]);

	useEffect(() => {
		const element = imageRef.current;

		if (!element || !src || hasImageError) {
			return;
		}

		if (element.complete && element.naturalWidth > 0) {
			setIsImageLoaded(true);
		}
	}, [src, hasImageError]);

	const handleImageLoad = useCallback(() => {
		setIsImageLoaded(true);
		setHasImageError(false);
	}, []);

	const handleImageError = useCallback(() => {
		setIsImageLoaded(false);
		setHasImageError(true);
	}, []);

	const imageProps = {
		ref: imageRef,
		alt,
		$visible: isImageLoaded && !hasImageError,
		$objectPosition: objectPosition,
		loading: priority ? 'eager' : loading,
		decoding: 'async',
		fetchPriority: priority ? 'high' : 'auto',
		onLoad: handleImageLoad,
		onError: handleImageError,
	};

	return (
		<MediaImageSlot className={className}>
			{showImageSkeleton && (
				<MediaImageSkeletonWrap aria-hidden="true">
					<Skeleton
						width="100%"
						height="100%"
						radius="0"
						minWidth="100%"
						minHeight="100%"
					/>
				</MediaImageSkeletonWrap>
			)}
			{src && !hasImageError && webpSrc ? (
				<picture>
					<source srcSet={webpSrc} type="image/webp" />
					<MediaImage {...imageProps} src={src} />
				</picture>
			) : null}
			{src && !hasImageError && !webpSrc ? (
				<MediaImage {...imageProps} src={src} />
			) : null}
		</MediaImageSlot>
	);
};

export default MediaImageLoader;
